import { useRoute } from 'wouter'
import { useEffect, useState, useMemo } from 'react'
import { enqueueSnackbar } from 'notistack'
import {
    MPSession,
    MPOrder,
    OrgOverview,
    Licence,
    HabitatAdminUser,
} from '@types'
import useForm from '@hooks/useForm'
import { DateTime } from 'luxon'
import { downloadBlob } from '@utils'
import { logout } from '@utils/sessions'
import API from '@enums/api'

interface FormValues {
    fromDate: DateTime | null | undefined
    toDate: DateTime | null | undefined
}

const useOrgPage = () => {
    const [_match, params] = useRoute('/organizations/:orgId')
    const [org, setOrg] = useState<OrgOverview | null>(null)
    const [users, setUsers] = useState<HabitatAdminUser[]>([])
    const [sessions, setSessions] = useState<MPSession[]>([])
    const [loadingOrg, setLoadingOrg] = useState(true)
    const [loadingUsers, setLoadingUsers] = useState(true)
    const [loadingSessions, setLoadingSessions] = useState(true)

    const [loadingOrders, setLoadingOrders] = useState(true)
    const [orders, setOrders] = useState<MPOrder[]>([])
    const [loadingLicences, setLoadingLicences] = useState(true)
    const [licences, setLicences] = useState<Licence[]>([])

    const [error, setError] = useState<string | null>(null)

    const form = useForm<FormValues>({ fromDate: null, toDate: null })

    useEffect(() => {
        if (params?.orgId) {
            const orgId = params.orgId
            fetchOrgData(orgId)
            fetchUsers(orgId)
            fetchSessionsAndOrders(orgId)
            fetchLicences(orgId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [params?.orgId])

    useEffect(() => {
        if (sessions.length > 0) {
            const { from, to } = getDateFilterInitialValues(sessions)

            form.setValues({
                fromDate: from,
                toDate: to,
            })
        }
    }, [sessions])

    const fetchOrgData = async (orgId: string) => {
        setLoadingOrg(true)
        try {
            const res = await API.ha.orgs.getOrg(orgId)

            if (!res.ok) {
                console.error(res.error)
                setError('Failed to fetch organization data')
            } else {
                const result = res.value
                setOrg(result)
            }
        } catch (err) {
            console.error(err)
            setError('Failed to fetch organization data')
        } finally {
            setLoadingOrg(false)
        }
    }

    const fetchUsers = async (orgId: string) => {
        setLoadingUsers(true)
        try {
            const res = await API.ha.users.getOrgUsers(orgId)

            if (!res.ok) {
                if (res.status === 401) {
                    return
                }
                console.error(res)
                setError('Failed to fetch users')
            } else {
                const result = res.value
                setUsers(result)
            }
        } catch (err) {
            console.log(err)
            setError('Failed to fetch users')
        } finally {
            setLoadingUsers(false)
        }
    }

    const fetchSessionsAndOrders = async (orgId: string) => {
        setLoadingSessions(true)
        setLoadingOrders(true)
        try {
            const res = await API.overviews.getOrdersAndSessions(orgId)

            if (!res.ok) {
                if (res.status === 401) {
                    return
                }
                console.error(res)
                setError('Failed to fetch sessions and orders data')
            } else {
                const result = res.value
                setSessions(result.sessions)
                setOrders(result.orders)
            }
        } catch (err) {
            console.error(err)
            setError('Failed to fetch sessions and orders data')
        } finally {
            setLoadingSessions(false)
            setLoadingOrders(false)
        }
    }

    const fetchLicences = async (orgId: string) => {
        setLoadingLicences(true)
        try {
            const res = await API.ha.licenses.getOrgLicenses(orgId)

            if (!res.ok) {
                if (res.status === 401) {
                    logout()
                    return
                }
                console.error(res)
                enqueueSnackbar('Failed to fetch licences', {
                    variant: 'warning',
                })
                return
            }

            const result = res.value
            setLicences(result)
        } catch (err) {
            console.error(err)
            enqueueSnackbar('Failed to fetch licences', { variant: 'warning' })
        } finally {
            setLoadingLicences(false)
        }
    }

    const sessionsChartData = useMemo(() => {
        const { fromDate, toDate } = form.values
        let filteredSessions = sessions

        if (fromDate && toDate) {
            filteredSessions = sessions.filter((session) => {
                const date = DateTime.fromISO(
                    session.createdAt as unknown as string
                )
                if (date < fromDate || date > toDate) return false
                return true
            })
        }

        const monthCounts: { [key: string]: number } = {}

        filteredSessions.forEach((session) => {
            const date = new Date(session.createdAt)
            if (!isNaN(date.getTime())) {
                const month = `${date.getFullYear()}-${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}`
                if (monthCounts[month]) {
                    monthCounts[month] += 1
                } else {
                    monthCounts[month] = 1
                }
            }
        })

        const sessionsOverTime = Object.keys(monthCounts)
            .sort()
            .map((month) => ({
                month,
                sessions: monthCounts[month],
            }))

        return { sessionsOverTime, filteredSessions }
    }, [sessions, form.values])

    const ordersChartData = useMemo(() => {
        const { fromDate, toDate } = form.values
        let filteredOrders = orders

        if (fromDate && toDate) {
            filteredOrders = orders.filter((order) => {
                const date = DateTime.fromISO(order.createdAt)

                if (date < fromDate || date > toDate) return false
                return true
            })
        }

        const monthCounts: { [key: string]: number } = {}

        filteredOrders.forEach((order) => {
            const date = new Date(order.createdAt)
            if (!isNaN(date.getTime())) {
                const month = `${date.getFullYear()}-${(date.getMonth() + 1)
                    .toString()
                    .padStart(2, '0')}`
                if (monthCounts[month]) {
                    monthCounts[month] += 1
                } else {
                    monthCounts[month] = 1
                }
            }
        })

        const ordersOverTime = Object.keys(monthCounts)
            .sort()
            .map((month) => ({
                month,
                orders: monthCounts[month],
            }))

        return { ordersOverTime, filteredOrders }
    }, [orders, form.values])

    const downloadReport = async () => {
        if (!org?.id || !form.values.fromDate || !form.values.toDate) {
            return
        }

        if (!form.values.fromDate.isValid || !form.values.toDate.isValid) {
            console.error('Invalid dates provided')
            return
        }

        const fromDate = form.values.fromDate.toISO()?.split('T')[0]
        const toDate = form.values.toDate.toISO()?.split('T')[0]

        try {
            const res = await API.overviews.downloadOrgReport(
                org!.id,
                fromDate!,
                toDate!
            )

            if (!res.ok) {
                if (res.status === 401) {
                    return
                }
                console.error(res)
            } else {
                const blob = res.value
                downloadBlob(blob, 'organization-report.xlsx')
            }
        } catch (error) {
            setError('Failed to download the report')
        }
    }

    return {
        org,
        downloadReport,
        sessionsChartData,
        ordersChartData,
        loading:
            loadingOrg ||
            loadingUsers ||
            loadingSessions ||
            loadingOrders ||
            loadingLicences,
        loadingOrg,
        loadingUsers,
        loadingSessions,
        loadingOrders,
        loadingLicences,
        error,
        form,
        sessions,
        orders,
        users,
        licences,
    }
}

export default useOrgPage

const getDateFilterInitialValues = (sessions: MPSession[]) => {
    const sessionDates = sessions
        .map((s) => new Date(s.createdAt))
        .filter((date) => !isNaN(date.getTime()))
        .sort((a, b) => a.getTime() - b.getTime())

    const from = DateTime.fromJSDate(sessionDates[0])
    const to = DateTime.now()
    return { from, to }
}
